/* eslint-disable max-lines */
import {
  AddGeneralPoolLanguage,
  AddProjectPoolLanguage,
  GetGeneralOverview,
  GetGeneralTranslations,
  GetProjectOverview,
  GetProjectTranslationData,
  Localization,
  SaveGeneralTranslations,
  SaveProjectTranslations,
  TranslateGeneralContentTranslation,
  TranslateProjectContentData,
} from '@customTypes';

import { api } from './api';

export const translationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getGeneralOverview: builder.query<GetGeneralOverview['Response'], void>({
      query: () => ({
        url: 'translations/general/overview',
      }),
      providesTags: [{ type: 'GeneralTranslations', id: 'overview' }],
    }),
    getProjectOverview: builder.query<GetProjectOverview['Response'], GetProjectOverview['Request']>({
      query: ({ businessId }) => ({
        url: `translations/projects/${businessId}/overview`,
      }),
      providesTags: [{ type: 'ProjectTranslations', id: 'overview' }],
    }),
    getGeneralTranslations: builder.query<GetGeneralTranslations['Response'], GetGeneralTranslations['Request']>({
      query: ({ languageId, ...params }) => ({
        url: `translations/general/languages/${languageId}`,
        params,
      }),
      providesTags: (result, error, { languageId, languagePool }) => [
        { type: 'GeneralTranslations', id: `general-${languageId}-${languagePool}` },
      ],
    }),
    translateGeneralContentTranslation: builder.mutation<
      TranslateGeneralContentTranslation['Response'],
      TranslateGeneralContentTranslation['Request']
    >({
      query: ({ languageId, body }) => ({
        url: `translations/general/languages/${languageId}/translate`,
        method: 'POST',
        body,
      }),
    }),
    getProjectTranslationData: builder.query<
      GetProjectTranslationData['Response'],
      GetProjectTranslationData['Request']
    >({
      query: ({ businessId, languageId, languagePool }) => ({
        url: `translations/projects/${businessId}/languages/${languageId}?pool=${languagePool}`,
      }),
      providesTags: (result, error, { businessId, languageId, languagePool }) => [
        { type: 'ProjectTranslations', id: `project-${businessId}-${languageId}-${languagePool}` },
      ],
    }),
    translateProjectContentData: builder.mutation<
      TranslateProjectContentData['Response'],
      TranslateProjectContentData['Request']
    >({
      query: ({ businessId, languageId, body }) => ({
        url: `translations/projects/${businessId}/languages/${languageId}/translate`,
        method: 'POST',
        body,
      }),
    }),
    addGeneralPoolLanguage: builder.mutation<void, AddGeneralPoolLanguage['Request']>({
      query: ({ languageId, languagePool }) => ({
        url: `translations/general/languages/${languageId}/new?pool=${languagePool}`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'GeneralTranslations', id: 'overview' }],
    }),
    addProjectPoolLanguage: builder.mutation<void, AddProjectPoolLanguage['Request']>({
      query: ({ businessId, languageId }) => ({
        url: `translations/projects/${businessId}/languages/${languageId}/new`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ProjectTranslations', id: 'overview' }],
    }),
    saveGeneralTranslations: builder.mutation<void, SaveGeneralTranslations['Request']>({
      query: ({ languageId, body }) => ({
        url: `translations/general/languages/${languageId}/all`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { languageId, languagePool }) => [
        { type: 'GeneralTranslations', id: `general-${languageId}-${languagePool}` },
      ],
    }),
    saveProjectTranslations: builder.mutation<void, SaveProjectTranslations['Request']>({
      query: ({ businessId, languageId, languagePool, body }) => ({
        url: `translations/projects/${businessId}/languages/${languageId}/all?pool=${languagePool}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { businessId, languageId, languagePool }) => [
        { type: 'ProjectTranslations', id: `project-${businessId}-${languageId}-${languagePool}` },
      ],
    }),
    getTranslations: builder.query<Localization['Response'], Localization['Request']>({
      query: (params) => ({
        url: 'localization',
        params,
      }),
    }),
  }),
});

export const {
  useGetGeneralOverviewQuery,
  useGetProjectOverviewQuery,
  useGetGeneralTranslationsQuery,
  useTranslateGeneralContentTranslationMutation,
  useGetProjectTranslationDataQuery,
  useTranslateProjectContentDataMutation,
  useAddGeneralPoolLanguageMutation,
  useAddProjectPoolLanguageMutation,
  useSaveGeneralTranslationsMutation,
  useSaveProjectTranslationsMutation,
  useGetTranslationsQuery,
} = translationApi;
